import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useFonts } from "expo-font";
import { NativeBaseProvider } from "native-base";
import React, { useEffect } from "react";
import { AppState } from "react-native";
import RNBootSplash from "react-native-bootsplash";
import { Provider as StoreProvider } from "react-redux";
import { PersistGate } from "redux-persist/integration/react";

import { AppBoundary } from "./boundaries/AppBoundary";
import ErrorBoundary from "./boundaries/ErrorBoundary";
import { CartControllerProvider } from "./contexts/CartControllerContext";
import { ViewportProvider } from "./contexts/ViewportContext";
import { useVersionCheck } from "./hooks/application/versionCheck";
import Navigation from "./navigation";
import analytics from "./services/analytics";
import { initializeLogger } from "./services/logger";
import { persistor, store } from "./store";
import { theme } from "./theme";
import { appFontsToLoad } from "./theme/fonts";
import { isNativeDevice } from "./utils/platform";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      cacheTime: 1000 * 60 * 60,
    },
  },
});

initializeLogger();

export default function App() {
  const [fontsLoaded] = useFonts(appFontsToLoad);
  const { checkForUpdates } = useVersionCheck();

  useEffect(() => {
    analytics.logEvent("app_open");
    checkForUpdates();
  }, []);

  useEffect(() => {
    const subscription = AppState.addEventListener("change", (nextAppState) => {
      if (nextAppState === "active") {
        checkForUpdates();
      }
    });
    return () => {
      subscription.remove();
    };
  }, []);

  useEffect(() => {
    async function hideSplash() {
      if (isNativeDevice) {
        await RNBootSplash.hide({ fade: true, duration: 500 });
      }
    }
    if (fontsLoaded) {
      hideSplash();
    }
  }, [fontsLoaded]);

  if (!fontsLoaded) {
    return null;
  }

  return (
    <ViewportProvider>
      <NativeBaseProvider theme={theme}>
        <ErrorBoundary>
          <StoreProvider store={store}>
            <PersistGate loading={null} persistor={persistor}>
              <QueryClientProvider client={queryClient}>
                <AppBoundary>
                  <CartControllerProvider>
                    <Navigation />
                  </CartControllerProvider>
                </AppBoundary>
              </QueryClientProvider>
            </PersistGate>
          </StoreProvider>
        </ErrorBoundary>
      </NativeBaseProvider>
    </ViewportProvider>
  );
}
